/**
 * ActorMarker — Agent vs Person from model pickup_kind / assigned resource (FE-ARCH-5).
 */

export type ActorKind = 'agent' | 'person';

export interface ActorMarker {
  kind: ActorKind;
  label: string;
  resourceRef?: string;
}

const AGENT_HINT =
  /^(agent|krybot|cursor|automation|bot|system|type:)/i;
const PERSON_HINT = /^(person:|human)/i;
/** Hosted resource-profile ids (`ai-ddd-rp-*`, `co-prh-*`) are people, not agents. */
const PERSON_RESOURCE_HINT = /(^|[-_])(rp|prh|resource-profile)([-_]|$)/i;

function normalizePickupKind(raw: string | undefined): ActorKind | null {
  if (!raw) return null;
  const v = raw.trim().toLowerCase();
  if (!v) return null;
  if (v === 'agent' || v === 'ai' || v === 'bot' || v === 'system') return 'agent';
  if (v === 'person' || v === 'human' || v === 'user') return 'person';
  return null;
}

function kindFromResourceHints(
  resourceRef: string | undefined,
  res: string | undefined
): ActorKind | null {
  const ref = (resourceRef ?? '').trim();
  const token = (res ?? '').trim();
  if (PERSON_HINT.test(ref) || ref.startsWith('person:')) return 'person';
  if (PERSON_RESOURCE_HINT.test(ref) || PERSON_RESOURCE_HINT.test(token)) return 'person';
  if (AGENT_HINT.test(ref) || AGENT_HINT.test(token)) return 'agent';
  if (token && !TYPE_PERSON_BUCKET.has(token.toLowerCase())) {
    // Named person tokens (non type-bucket) default to person when unlabeled.
    if (!token.includes(':') && !/^ai-(krybot|cursor|agent)\b/i.test(token)) return 'person';
  }
  return null;
}

const TYPE_PERSON_BUCKET = new Set(['leadership', 'growth', 'success', 'engineering']);

export interface ActorMarkerInput {
  pickupKind?: string;
  assignedResourceRef?: string;
  assignedResourceLabel?: string;
  res?: string;
}

/**
 * Derive ActorMarker. Returns null when no actor signal exists (unassigned).
 * Prefer explicit pickup_kind; fall back to resource ref / res heuristics only.
 */
export function deriveActorMarker(input: ActorMarkerInput): ActorMarker | null {
  const fromPickup = normalizePickupKind(input.pickupKind);
  const fromHints = kindFromResourceHints(input.assignedResourceRef, input.res);
  const kind = fromPickup ?? fromHints;
  if (!kind) return null;

  const label =
    input.assignedResourceLabel?.trim() ||
    (input.assignedResourceRef?.startsWith('person:')
      ? input.assignedResourceRef.slice(7)
      : undefined) ||
    input.res?.trim() ||
    (kind === 'agent' ? 'Agent' : 'Person');

  return {
    kind,
    label,
    resourceRef: input.assignedResourceRef?.trim() || undefined,
  };
}

export function actorMarkerLegendLabel(kind: ActorKind): string {
  return kind === 'agent' ? 'Agent' : 'Person';
}
